import React from 'react';

export type TripType = 'PRIORITY' | 'REGULAR';

interface TripTypeSelectorProps {
  value: TripType;
  onChange: (value: TripType) => void;
  disabled?: boolean;
  showPricing?: boolean;
  priorityPrice?: number;
  regularPrice?: number;
  hidePriority?: boolean;
}

const TripTypeSelector: React.FC<TripTypeSelectorProps> = ({
  value,
  onChange,
  disabled = false,
  showPricing = false,
  priorityPrice = 2.00, // REDIPAY INTEGRATION: Updated to RM2.00 flat rate
  regularPrice = 2.00, // REDIPAY INTEGRATION: Updated to RM2.00 flat rate
  hidePriority = false,
}) => {
  const tripTypes = [
    {
      value: 'PRIORITY' as TripType,
      label: 'Priority',
      description: 'Immediate dispatch, emergency response',
      price: priorityPrice,
      // Removed emoji icon
      color: 'red',
    },
    {
      value: 'REGULAR' as TripType,
      label: 'Regular',
      description: 'Standard scheduling within 24 hours',
      price: regularPrice,
      // Removed emoji icon
      color: 'blue',
    },
  ];

  const visibleTripTypes = hidePriority
    ? tripTypes.filter((t) => t.value !== 'PRIORITY')
    : tripTypes;

  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium text-gray-700">
        Trip Type
      </label>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {visibleTripTypes.map((tripType) => (
          <div
            key={tripType.value}
            className={`
              relative rounded-lg border-2 p-4 cursor-pointer transition-all
              ${value === tripType.value
                ? `border-${tripType.color}-500 bg-${tripType.color}-50`
                : 'border-gray-200 hover:border-gray-300'
              }
              ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
            `}
            onClick={() => !disabled && onChange(tripType.value)}
          >
            {/* Radio Button */}
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 pt-1">
                <input
                  type="radio"
                  name="tripType"
                  value={tripType.value}
                  checked={value === tripType.value}
                  onChange={() => onChange(tripType.value)}
                  disabled={disabled}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                />
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-medium text-gray-900">
                  {tripType.label}
                </h3>

                <p className="mt-1 text-sm text-gray-600">
                  {tripType.description}
                </p>

                {showPricing && (
                  <p className="mt-2 text-lg font-semibold text-gray-900">
                    RM {tripType.price}
                  </p>
                )}
              </div>
            </div>

            {/* Selected indicator */}
            {value === tripType.value && (
              <div className={`absolute top-2 right-2 w-6 h-6 bg-${tripType.color}-500 rounded-full flex items-center justify-center`}>
                <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Priority notice */}
      {value === 'PRIORITY' && (
        <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Priority Response
              </h3>
              <div className="mt-1 text-sm text-red-700">
                <p>Priority service includes:</p>
                <ul className="mt-1 list-disc list-inside text-sm text-red-700">
                  <li>Immediate dispatch (under 30 minutes)</li>
                  <li>Emergency medical equipment</li>
                  <li>Dedicated driver and medical staff</li>
                  <li>Priority traffic clearance</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Regular notice */}
      {value === 'REGULAR' && (
        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">
                Standard Service
              </h3>
              <div className="mt-1 text-sm text-blue-700">
                <p>Regular service includes:</p>
                <ul className="mt-1 list-disc list-inside text-sm text-blue-700">
                  <li>Standard medical transport</li>
                  <li>Basic medical equipment</li>
                  <li>Scheduled within 24 hours</li>
                  <li>Experienced medical staff</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TripTypeSelector;